import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useDeployService } from "../../api/queries";
import type { Service } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { StatePill } from "../../components/StatePill";
import "../../components/Form.css";
import "./deployProjectDialog.css";

type RowStatus = "pending" | "deploying" | "succeeded" | "failed";

interface Row {
  service: Service;
  status: RowStatus;
  error?: string;
}

function RowStatusPill({ status }: { status: RowStatus }) {
  if (status === "succeeded") return <StatePill label="Deployed" tone="positive" shape="dot" />;
  if (status === "failed") return <StatePill label="Failed" tone="negative" shape="triangle" />;
  if (status === "deploying") return <StatePill label="Deploying" tone="info" shape="spin" />;
  return <StatePill label="Pending" tone="neutral" shape="ring" />;
}

/**
 * Confirms a batch "Deploy {project}" before it fans out (UX polish wave 2 decision 4):
 * `ProjectDetailPage`'s header button used to fire every undeployed service's `deployService`
 * at once with no list of what was about to happen and no visible per-service result — just a
 * toast after the fact. This dialog adds the missing steps around the SAME per-service calls
 * (each still its own individually authorized/audited `deployService` request, S-11 — this
 * never becomes one fabricated "deploy project" endpoint): a confirm screen naming every
 * service that will deploy, a live per-row progress list while it runs, and a completion
 * summary that names any failures instead of a single aggregate toast.
 */
export function DeployProjectDialog({
  open,
  projectId,
  projectName,
  services,
  onClose,
}: {
  open: boolean;
  projectId: string;
  projectName: string;
  /** The project's currently-undeployed services — computed by the caller from `Service.status
   * === "created"` (A-5: this dialog doesn't re-derive its own copy of that rule). */
  services: Service[];
  onClose: () => void;
}) {
  const deployMutation = useDeployService(projectId);
  const [phase, setPhase] = useState<"confirm" | "running" | "done">("confirm");
  const [rows, setRows] = useState<Row[]>([]);

  // Re-seed to a fresh confirm screen every time the dialog opens on a (possibly
  // different) undeployed-services set — never carries a stale run's rows into a new open.
  useEffect(() => {
    if (open) {
      setPhase("confirm");
      setRows(services.map((service) => ({ service, status: "pending" })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function updateRow(serviceId: string, patch: Partial<Row>) {
    setRows((current) => current.map((row) => (row.service.id === serviceId ? { ...row, ...patch } : row)));
  }

  function handleConfirm() {
    setPhase("running");
    Promise.allSettled(
      rows.map(async ({ service }) => {
        updateRow(service.id, { status: "deploying" });
        try {
          await deployMutation.mutateAsync({ id: service.id });
          updateRow(service.id, { status: "succeeded" });
        } catch (err) {
          updateRow(service.id, { status: "failed", error: err instanceof Error ? err.message : "Deploy failed." });
        }
      }),
    ).then(() => setPhase("done"));
  }

  function handleClose() {
    // A run in flight keeps going server-side regardless (each deploy request is already
    // accepted) — closing here only dismisses the dialog, never cancels anything real.
    onClose();
  }

  const succeededCount = rows.filter((r) => r.status === "succeeded").length;
  const failedRows = rows.filter((r) => r.status === "failed");

  return (
    <Modal open={open} title={`Deploy ${projectName}`} onClose={handleClose}>
      {phase === "confirm" && (
        <div className="deploy-project-dialog">
          <p>
            This deploys every service in {projectName} that hasn't been deployed yet — {rows.length} service
            {rows.length === 1 ? "" : "s"}:
          </p>
          <ul className="deploy-project-dialog__list">
            {rows.map(({ service }) => (
              <li key={service.id}>{service.name}</li>
            ))}
          </ul>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleConfirm}>
              Deploy {rows.length} service{rows.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      {(phase === "running" || phase === "done") && (
        <div className="deploy-project-dialog">
          <ul className="deploy-project-dialog__progress" aria-live="polite">
            {rows.map(({ service, status, error }) => (
              <li key={service.id} className="deploy-project-dialog__progress-row">
                <span className="deploy-project-dialog__progress-name">{service.name}</span>
                <RowStatusPill status={status} />
                {status === "failed" && error && (
                  <span className="deploy-project-dialog__progress-error">{error}</span>
                )}
              </li>
            ))}
          </ul>

          {phase === "done" && (
            <>
              <p className="deploy-project-dialog__summary">
                {failedRows.length === 0
                  ? `Deployed ${succeededCount} service${succeededCount === 1 ? "" : "s"}.`
                  : `Deployed ${succeededCount} of ${rows.length} services. Failed: ${failedRows
                      .map((r) => r.service.name)
                      .join(", ")}.`}
              </p>
              <div className="form-actions">
                <Link
                  to={`/projects/${projectId}/services`}
                  className="button button--primary"
                  onClick={handleClose}
                >
                  View in Services
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
